import { RateRow } from "../Rows/RateRow";
import { useForm } from "react-hook-form";
import { useMemo, useEffect } from "react";
import CombinationFilter from "../Filters/CombinationFilter";
import SortDropdown from "../Filters/SortDropdown";
import { formatCurrency } from "../../utils/formatCurrency";
import { TableHeaderWithTooltip } from "../Tooltips/TableHeaderWithTooltip";

export function RatesTable({ rates, onSelect, selectedRate }) {
  /* -------------------- DERIVED DATA -------------------- */

  const uniqueRates = useMemo(() => {
    const seen = new Set();
    return rates.filter((rate) => {
      if (seen.has(rate.serviceId)) return false;
      seen.add(rate.serviceId);
      return true;
    });
  }, [rates]);

  const carrierOptions = useMemo(() => {
    return [...new Set(rates.map((r) => r.carrierName))].map((carrier) => ({
      label: carrier,
      value: carrier,
    }));
  }, [rates]);

  const allServiceOptions = useMemo(() => {
    return carrierOptions.map((carrier) => ({
      label: carrier.label,
      options: [
        ...new Set(
          rates
            .filter((r) => r.carrierName === carrier.value)
            .map((r) => r.serviceName)
        ),
      ].map((service) => ({ label: service, value: service })),
    }));
  }, [rates, carrierOptions]);

  const transitDaysRange = useMemo(() => {
    const days = rates.map((r) => parseInt(r.transitDays));
    return { min: Math.min(...days), max: Math.max(...days) };
  }, [rates]);

  const priceRange = useMemo(() => {
    const prices = rates.map((r) => parseFloat(r.totalCharge));
    return {
      min: Math.floor(Math.min(...prices)),
      max: Math.ceil(Math.max(...prices)),
    };
  }, [rates]);

  /* -------------------- FORM -------------------- */

  const { control, setValue, watch, getValues } = useForm({
    defaultValues: {
      carrierFilter: carrierOptions.map((o) => o.value),
      serviceFilter: allServiceOptions.flatMap((g) =>
        g.options.map((o) => o.value)
      ),
      transitDaysFilter: [transitDaysRange.min, transitDaysRange.max],
      priceRangeFilter: [priceRange.min, priceRange.max],
      sortOrder: "asc",
    },
  });

  const selectedCarriers = watch("carrierFilter");
  const serviceFilter = watch("serviceFilter");

  const transitDaysFilter = watch("transitDaysFilter");
  const priceRangeFilter = watch("priceRangeFilter");
  const sortOrder = watch("sortOrder");

  /* -------------------- FIXED EFFECT (NO LOOP) -------------------- */

  useEffect(() => {
    if (!selectedCarriers?.length) return;

    const carrierToServices = allServiceOptions.reduce((acc, group) => {
      acc[group.label] = group.options.map((o) => o.value);
      return acc;
    }, {});

    const nextServices = selectedCarriers.flatMap(
      (carrier) => carrierToServices[carrier] || []
    );

    // get current serviceFilter value without adding it to deps
    const currentServiceFilter = getValues("serviceFilter") || [];

    const currentSet = new Set(currentServiceFilter);
    const nextSet = new Set(nextServices);

    const isSame =
      currentSet.size === nextSet.size &&
      [...currentSet].every((v) => nextSet.has(v));

    if (isSame) return;

    setValue("serviceFilter", nextServices);
  }, [selectedCarriers, allServiceOptions, setValue, getValues]);

  /* -------------------- FILTER OPTIONS -------------------- */

  const serviceOptions = useMemo(() => {
    return allServiceOptions.filter((group) =>
      selectedCarriers.includes(group.label)
    );
  }, [allServiceOptions, selectedCarriers]);

  const filters = [
    {
      type: "multiSelect",
      name: "carrierFilter",
      label: "Carrier",
      options: carrierOptions,
      isGrouped: false,
    },
    {
      type: "multiSelect",
      name: "serviceFilter",
      label: "Service",
      options: serviceOptions,
      isGrouped: true,
    },
    {
      type: "range",
      name: "transitDaysFilter",
      label: "Transit Days",
      min: transitDaysRange.min,
      max: transitDaysRange.max,
      onChange: (value) => setValue("transitDaysFilter", value),
      trackColor: "#0052FF",
      handleColor: "#0052FF",
      railColor: "#f8f8f8",
      formatLabel: (val) => `${val} days`,
    },
    {
      type: "range",
      name: "priceRangeFilter",
      label: "Price Range",
      min: priceRange.min,
      max: priceRange.max,
      step: 1,
      onChange: (value) => setValue("priceRangeFilter", value),
      trackColor: "#0052FF",
      handleColor: "#0052FF",
      railColor: "#f8f8f8",
      formatLabel: formatCurrency,
    },
  ];

  /* -------------------- FILTER + SORT -------------------- */

  const filteredAndSortedRates = useMemo(() => {
    const filtered = uniqueRates.filter(
      (rate) =>
        selectedCarriers.includes(rate.carrierName) &&
        serviceFilter.includes(rate.serviceName) &&
        parseInt(rate.transitDays) >= transitDaysFilter[0] &&
        parseInt(rate.transitDays) <= transitDaysFilter[1] &&
        parseFloat(rate.totalCharge) >= priceRangeFilter[0] &&
        parseFloat(rate.totalCharge) <= priceRangeFilter[1]
    );

    return [...filtered].sort((a, b) => {
      const diff = parseFloat(a.totalCharge) - parseFloat(b.totalCharge);
      return sortOrder === "asc" ? diff : -diff;
    });
  }, [
    uniqueRates,
    selectedCarriers,
    serviceFilter,
    transitDaysFilter,
    priceRangeFilter,
    sortOrder,
  ]);

  const minCharge = Math.min(
    ...filteredAndSortedRates.map((r) => parseFloat(r.totalCharge))
  );

  /* -------------------- RENDER -------------------- */

  return (
    <>
      {/* Filters */}
      <div className="flex flex-wrap items-center justify-end mb-4 p-4 gap-4">
        <SortDropdown
          name="sortOrder"
          control={control}
          setValue={setValue}
          className="bg-gray-50 border border-gray-300 rounded-xl"
        />
        <CombinationFilter
          filters={filters}
          control={control}
          inputClassName="bg-gray-50 border border-gray-300 rounded-xl"
        />
      </div>

      {/* Table */}
      <div className="bg-white shadow-sm rounded-xl border border-gray-200">
        {/* Header */}
        <div className="rounded-t-xl grid grid-cols-12 gap-10 px-4 py-4 bg-gray-50 text-gray-700 text-xs font-semibold uppercase tracking-wide items-start">
          <div className="col-span-3">
            <TableHeaderWithTooltip label="Carrier & Service" />
          </div>
          <div className="col-span-2">
            <TableHeaderWithTooltip
              label="Transit"
              tooltip="Estimated delivery time. Actual delivery may be faster or slower. Most carriers have a cut off time of 5pm. Dropoffs after 5pm will delivery the following business day."
            />
          </div>
          <div className="col-span-3">
            <TableHeaderWithTooltip
              label="Delivery Options"
              tooltip="How you will hand over the package to the carrier."
            />
          </div>
          <div className="col-span-3">
            <TableHeaderWithTooltip label="Price" />
          </div>
          <div className="col-span-1" />
        </div>

        {/* Rows */}
        <div>
          {filteredAndSortedRates.map((rate) => (
            <RateRow
              key={rate.serviceId}
              rate={rate}
              onSelect={onSelect}
              selectedRate={selectedRate}
              isCheapest={parseFloat(rate.totalCharge) === minCharge}
            />
          ))}
        </div>
      </div>
    </>
  );
}
